// eraserWidth — 消しゴムの独立サイズ「2 バケット方式」の pure helper
// (consult 2026-05-24 / docs/_wip/eraser-separate-size.md).
//
// ## 背景・経緯
//
// 新版は `pen` state が単一オブジェクト `{ pen, width, color, alpha, mask }`
// (`ClassicLayout.tsx`) で width / color / alpha を全ペン種で共有していた.
// ペン種切替は `setPen(p => ({...p, pen: kind}))` で width を据え置くため、
// 消しゴムに持ち替えるとブラシの太さをそのまま引き継いでしまう
// (= プロ系 PS / CSP / SAI ではツール毎に独立サイズを持つのが標準). 旧 Java 版にも
// `drawWidths`/`eraseWidths` の痕跡 (`ChatPanel.java:1490` コメントアウト) があり、
// draw/erase で幅を分ける発想は元々あった.
//
// ## 決定 (consult 2026-05-24) = 案 A: 2 バケット方式
//
// サイズを「**描画用サイズ** (brush/flat/marker で共有)」と「**消しゴム用サイズ**
// (独立)」の 2 本に分ける. **color / alpha は global 共有のまま** (描画ツール
// 切替で色が変わる鬱陶しさを避ける = 定石).
//
// - 実効 width = `pen === 'eraser' ? eraserWidth : drawWidth` (= `resolveActiveWidth`).
// - 太さ変更 (太さ調整バー / 6 プリセット cell 選択 / 微調整) は **現在アクティブ
//   なバケット** (描画 or 消しゴム) に効く (= `isEraserActive` で振り分け).
// - ペン種切替時、実効 width はアクティブバケットから derive されるだけ
//   (= 明示コピー不要. caller は `pen.width` を実効値に同期するだけで下流
//   `DrawingCanvas` / `BrushCursorOverlay` は無改修).
// - 6 プリセットの **値そのもの + 選択中 cell index もバケット別** になった
//   (2026-05-27 consult). 消しゴムは専用プリセット `[2,5,10,20,40,60]`
//   (`DEFAULT_ERASER_PEN_WIDTHS`) を持ち、初期は最大(60)を選択する. 描画用
//   `[1,3,5,10,20,40]` (`DEFAULT_PEN_WIDTHS` / 初期 index 0 = 値 1) は不変.
//   描画中に消しゴムの、消しゴム中に描画のプリセット/index/width を絶対に
//   書き換えない (= `applyWidthToActiveBucket` の思想をプリセット/index にも拡張).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM** なので React component は
// 単体テストできない. バケット振り分けロジックを pure 関数に切り出して
// 単体テスト可能にする (= `penOnlyMode.ts` / `spuit.ts` と同じ流儀).
//
// ## 永続・スコープ外
//
// - 永続は **in-memory のみ** (R11「penWidths は in-memory」踏襲). リロードで
//   初期値に戻る. 要望が出たら別途永続化を検討.
// - **protocol 不変 / server 不変**: width は stroke ごとに送るだけの純 client
//   UI state. 描画エンジン (CanvasDrawingEngine / worker) 非関与.

import type { StrokePen } from '@oekaki/protocol';

/**
 * 現在アクティブなバケットが「消しゴム」かどうか.
 *
 * `pen === 'eraser'` のときだけ消しゴムバケット (= `eraserWidth`) が効く.
 * brush / flat / marker はいずれも描画バケット (= `drawWidth`) を共有する.
 */
export const isEraserActive = (pen: StrokePen): boolean => pen === 'eraser';

/**
 * ペン種に応じた実効 width を 2 バケットから derive する.
 *
 * - `pen === 'eraser'`  → `eraserWidth` (= 消しゴム独立サイズ).
 * - それ以外 (brush / flat / marker) → `drawWidth` (= 描画ツール共有サイズ).
 *
 * caller (= `ClassicLayout`) は毎 render この実効値を `pen.width` に同期する
 * ことで、下流 (`DrawingCanvas` / `BrushCursorOverlay`) を無改修に保つ.
 *
 * @param pen 現在のペン種.
 * @param drawWidth 描画ツール共有の太さ.
 * @param eraserWidth 消しゴム独立の太さ.
 */
export const resolveActiveWidth = (
  pen: StrokePen,
  drawWidth: number,
  eraserWidth: number,
): number => (isEraserActive(pen) ? eraserWidth : drawWidth);

/**
 * 2 バケット (描画 / 消しゴム) の実効サイズ状態.
 *
 * 2026-05-27 consult: 「選択中の太さ」に加え、**6 プリセットの値 + 選択中
 * cell index もバケット別** になった (= `ClassicLayout` 側で描画用 / 消しゴム用
 * の `PenWidthState` を 2 本持つ). この `WidthBuckets` は実効 width
 * (`pen.width` への同期元) だけを保持する. 初期値は描画 = `DEFAULT_PEN_CONFIG.width`
 * (=1)、消しゴム = 60 (= `DEFAULT_ERASER_PEN_WIDTHS` 末尾).
 */
export interface WidthBuckets {
  readonly drawWidth: number;
  readonly eraserWidth: number;
}

/**
 * 太さ変更を「現在アクティブなバケット」にだけ適用した次の状態を返す.
 *
 * 太さ調整バー / 6 プリセット cell 選択 / 微調整いずれの経路でも、現在の
 * `pen` 種に応じて draw / eraser のどちらか一方だけを更新する (= 非アクティブ
 * バケットの太さは保持される). これがバケット別保持の核心:
 *   太いブラシ → 消しゴム (細い別サイズ) → ブラシ戻りで太さが復元.
 *
 * @param buckets 現在の 2 バケット.
 * @param pen 現在のペン種 (= どちらのバケットに効かせるかの判定).
 * @param nextWidth 新しい太さ (= 呼び出し側で clamp 済を想定).
 */
export const applyWidthToActiveBucket = (
  buckets: WidthBuckets,
  pen: StrokePen,
  nextWidth: number,
): WidthBuckets =>
  isEraserActive(pen)
    ? { ...buckets, eraserWidth: nextWidth }
    : { ...buckets, drawWidth: nextWidth };
